/**
 * Capacitor Mobile Features for Framecast News
 * Handles push notifications, offline storage, sharing, and network status
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Share } from '@capacitor/share';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

// Check if running on native platform
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

// ============================================
// Push Notifications
// ============================================

export const initPushNotifications = async (onNotificationReceived) => {
  if (!isNative()) return { success: false, reason: 'not-native' };

  try {
    // Request permission
    let permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      return { success: false, reason: 'permission-denied' };
    }

    // Register for push notifications
    await PushNotifications.register();

    // Listen for registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration success, token:', token.value);
      // Send token to backend for storage
      sendPushTokenToBackend(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Listen for incoming notifications when app is open
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      if (onNotificationReceived) {
        onNotificationReceived(notification);
      }
    });

    // Listen for notification taps
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      // Navigate to story if cluster_id is in the data
      const data = action.notification.data;
      if (data?.cluster_id) {
        window.location.href = `/stories?expand=${data.cluster_id}`;
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Push notification init error:', error);
    return { success: false, reason: error.message };
  }
};

const sendPushTokenToBackend = async (token) => {
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${apiUrl}/api/v1/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        token, 
        platform: getPlatform(),
        device_id: await getDeviceId()
      }),
      credentials: 'include'
    });
  } catch (error) {
    console.error('Failed to send push token to backend:', error);
  }
};

// ============================================
// Native Sharing
// ============================================

export const shareStory = async (story, shareUrl) => {
  const shareData = {
    title: story.title,
    text: `Check out how global media frames this story: "${story.title}"`,
    url: shareUrl,
    dialogTitle: 'Share this story'
  };

  if (isNative()) {
    try {
      await Share.share(shareData);
      return { success: true };
    } catch (error) {
      console.error('Native share error:', error);
      return { success: false, reason: error.message };
    }
  } else {
    // Web fallback
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return { success: true };
      } catch (error) {
        return { success: false, reason: error.message };
      }
    } else {
      // Copy to clipboard fallback
      try {
        await navigator.clipboard.writeText(shareUrl);
        return { success: true, method: 'clipboard' };
      } catch (error) {
        return { success: false, reason: 'clipboard-failed' };
      }
    }
  }
};

// ============================================
// Offline Storage
// ============================================

const CACHE_KEYS = {
  STORIES: 'cached_stories',
  CATEGORIES: 'cached_categories',
  FEATURED: 'cached_featured',
  LAST_SYNC: 'last_sync_time'
};

export const cacheStories = async (stories) => {
  try {
    await Preferences.set({
      key: CACHE_KEYS.STORIES,
      value: JSON.stringify(stories)
    });
    await Preferences.set({
      key: CACHE_KEYS.LAST_SYNC,
      value: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Cache stories error:', error);
    return { success: false };
  }
};

export const getCachedStories = async () => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.STORIES });
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error('Get cached stories error:', error);
    return null;
  }
};

export const cacheFeaturedStory = async (story) => {
  try {
    await Preferences.set({
      key: CACHE_KEYS.FEATURED,
      value: JSON.stringify(story)
    });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const getCachedFeatured = async () => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.FEATURED });
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

export const getLastSyncTime = async () => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEYS.LAST_SYNC });
    return value;
  } catch (error) {
    return null;
  }
};

export const clearCache = async () => {
  try {
    await Preferences.clear();
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

// ============================================
// Network Status
// ============================================

let networkStatusCallback = null;

export const initNetworkListener = (callback) => {
  networkStatusCallback = callback;
  
  Network.addListener('networkStatusChange', (status) => {
    console.log('Network status changed:', status);
    if (networkStatusCallback) {
      networkStatusCallback(status);
    }
  });
};

export const getNetworkStatus = async () => {
  try {
    const status = await Network.getStatus();
    return status;
  } catch (error) {
    return { connected: true, connectionType: 'unknown' };
  }
};

// ============================================
// App Lifecycle
// ============================================

export const initAppLifecycle = (onResume, onPause) => {
  if (!isNative()) return;

  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive && onResume) {
      onResume();
    } else if (!isActive && onPause) {
      onPause();
    }
  });

  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });
};

// ============================================
// Device ID (for push notification tracking)
// ============================================

export const getDeviceId = async () => {
  try {
    const { value } = await Preferences.get({ key: 'device_id' });
    if (value) return value;
    
    // Generate new device ID
    const newId = 'device_' + Math.random().toString(36).substring(2, 15);
    await Preferences.set({ key: 'device_id', value: newId });
    return newId;
  } catch (error) {
    return 'unknown';
  }
};

// ============================================
// Status Bar & Splash Screen
// ============================================

export const initNativeUI = async () => {
  if (!isNative()) return;

  try {
    // Set status bar style
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#1a1a1a' });
    
    // Hide splash screen after a delay
    setTimeout(async () => {
      await SplashScreen.hide();
    }, 500);
  } catch (error) {
    console.error('Native UI init error:', error);
  }
};

// ============================================
// Initialize All Mobile Features
// ============================================

export const initMobileFeatures = async (options = {}) => {
  if (!isNative()) {
    console.log('Running in web mode, skipping native initialization');
    return;
  }

  console.log(`Initializing mobile features for ${getPlatform()}`);

  // Initialize native UI
  await initNativeUI();

  // Initialize push notifications
  if (options.onNotificationReceived) {
    await initPushNotifications(options.onNotificationReceived);
  }

  // Initialize network listener
  if (options.onNetworkChange) {
    initNetworkListener(options.onNetworkChange);
  }

  // Initialize app lifecycle
  if (options.onResume || options.onPause) {
    initAppLifecycle(options.onResume, options.onPause);
  }

  console.log('Mobile features initialized');
};

export default {
  isNative,
  getPlatform,
  initMobileFeatures,
  initPushNotifications,
  shareStory,
  cacheStories,
  getCachedStories,
  cacheFeaturedStory,
  getCachedFeatured,
  getNetworkStatus,
  initNetworkListener,
  clearCache
};
